import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { PriceMovementChart } from '@terminal/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { ScenariosData } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { getPredictionsTableData } from '@terminal/views/turbo-chart/components/insights/utils/insights-utils/insights-utils';
import { PredictionSummaryTable } from '@terminal/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ScenarioPriceMovement.module.scss';

export interface ScenarioPriceMovementProps {
  scenario: ScenariosData;
  bestHorizon: string;
  horizons: string[];
  isFixedIncome: boolean;
}

const SCENARIO_DRAWER_CHART_HEIGHT = 168;

export const ScenarioPriceMovement = ({
  scenario,
  bestHorizon,
  horizons,
  isFixedIncome,
}: ScenarioPriceMovementProps) => {
  const { t } = useTranslation('scenario');

  const [activeLabelIndex, setActiveLabelIndex] = useState(0);
  const labels = [t('drawer.buttonGroup.median'), t('drawer.buttonGroup.mean')];
  const legendLabel = labels[activeLabelIndex];
  const isMedian = activeLabelIndex === 0;
  const predictionsMapped = scenario.predictions.map(p => {
    return {
      high: p.high,
      median: isMedian ? p.median : p.mean,
      low: p.low,
      relative_idx: p.relative_idx,
    };
  });

  const predictionByHorizon = useMemo(() => {
    return getPredictionsTableData(horizons, scenario.predictions);
  }, [scenario.predictions, horizons]);

  return (
    <div
      className={styles.scenarioPriceMovementRoot}
      data-testid="scenario-price-movement"
    >
      <ButtonGroup
        className={styles.styledButtonGroup}
        labels={labels}
        onClick={setActiveLabelIndex}
        activeIndex={activeLabelIndex}
      />

      <PriceMovementChart
        prediction={predictionsMapped}
        bestHorizon={bestHorizon}
        isFixedIncome={isFixedIncome}
        horizonToIndexOffset={1}
        maxHeight={SCENARIO_DRAWER_CHART_HEIGHT}
        legendLabel={legendLabel}
        isMedian={isMedian}
        isShowCumulativeReturn={false}
        useDynamicDaysLabel
        defaultHorizons={horizons}
        hiddenHighlightedHorizonLabels={['2W']}
        useSimpleRightAxis
      />
      <PredictionSummaryTable
        data={predictionByHorizon}
        horizons={horizons}
        isMedian={isMedian}
        isFixedIncome={isFixedIncome}
      />
    </div>
  );
};
